// ============================================================================
// The separation record — ONE checklist, rendered four ways.
//
// ⛔ WHO SEES WHAT IS DECIDED BY THE DATABASE, NOT HERE. RLS on
// separation_checklist_items returns every row to HR, Executive and Finance and
// only the person's own rows to everybody else, and RLS on employee_separations
// returns the record itself only to the first group. So:
//
//   record === null  ⇒ this person holds items but may not read the record.
//                      They get the personal view and nothing else. There is no
//                      client-side redaction here to forget to write, because
//                      the reason, the documents and the pay figures never
//                      arrived in the browser.
//
// The four tabs are FILTERS over one array. Consolidated, By owner and Blocking
// are three arrangements of the same rows; My items is the same rows filtered to
// the signed-in person. Nothing is reworded per audience.
// ============================================================================

import { canWriteHrRecord } from '../../shared/role-gates';
import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { AlertTriangle, Download, FileText } from 'lucide-react';
import {
  Button,
  Card,
  ErrorState,
  PageHeader,
  Skeleton,
  TabBar,
  useToast,
} from '../../shared/components';
import { useAuth } from '../../shared/auth';
import { errorMessage } from '../../shared/errors';
import { ChecklistItemRow } from './checklist-item';
import { EditDatesButton, MissingDateBanner } from './edit-dates';
import { HandoffScreen } from './handoff-screen';
import {
  completeSeparation,
  publishPayrollTotal,
  useSeparationRecord,
} from './hooks/use-separation-data';
import {
  GROUP_LABELS,
  GROUP_ORDER,
  OWNER_LABELS,
  reasonLabel,
  reasonSpec,
  type ChecklistItem,
} from './types';

type TabId = 'consolidated' | 'mine' | 'owner' | 'blocking';

function fmtDate(d: string | null | undefined): string {
  if (!d) return '—';
  const dt = new Date(d + 'T00:00:00Z');
  if (Number.isNaN(dt.getTime())) return '—';
  return dt.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  });
}

export default function SeparationRecord() {
  const { separationId } = useParams<{ separationId: string }>();
  const { profile } = useAuth();
  const toast = useToast();
  const { record, items, names, loading, error, refetch } = useSeparationRecord(separationId);
  const [handoff, setHandoff] = useState<ChecklistItem | null>(null);
  const [payroll, setPayroll] = useState('');
  const [busy, setBusy] = useState(false);

  const myEid = profile?.employee_id ?? null;
  // Mirrors hr_assert_may_write: HR and superuser hold the pen on the record
  // itself. The RPC is the real gate; this only decides whether to draw a button.
  const canWriteHr = canWriteHrRecord(profile);

  // "Mine" is a filter over the SAME rows. An item with no explicit assignee
  // still counts as mine when I hold the position that owns it — and the only
  // way that row reached this browser at all is that RLS resolved the position
  // to me, so no second resolution is needed here.
  const mine = useMemo(
    () =>
      items.filter(
        (i) => (myEid && i.assigned_to_eid === myEid) || (!i.assigned_to_eid && !!myEid),
      ),
    [items, myEid],
  );

  // The record itself is readable only by HR, Executive and Finance. If it came
  // back, this person is one of them.
  const canSeeConsolidated = !!record;
  const [tab, setTab] = useState<TabId>('mine');
  const effectiveTab: TabId = canSeeConsolidated ? tab : 'mine';

  const openItems = items.filter((i) => !i.completed_at);
  const blocking = openItems.filter((i) => i.is_blocking);
  const doneCount = items.length - openItems.length;
  const spec = reasonSpec(record?.separation_type);
  const leaverName = record ? (names.get(record.employee_id) ?? record.employee_id) : null;

  if (loading) {
    return (
      <div className="mx-auto max-w-5xl space-y-4 p-6">
        <Skeleton className="h-10 w-72" />
        <Skeleton className="h-64" />
      </div>
    );
  }
  if (error) return <ErrorState title="Couldn't load this separation" body={error} />;

  if (!record && items.length === 0) {
    return (
      <div className="mx-auto max-w-3xl p-6">
        <Card className="p-6">
          <h1 className="text-base font-semibold text-tcs-text">Nothing here for you</h1>
          <p className="mt-2 text-sm text-tcs-text-secondary">
            This separation exists, but you hold no items on it and the record itself is
            visible to HR, the executives and Finance only. If you were expecting an item,
            ask whoever assigned it to check it went to you.
          </p>
        </Card>
      </div>
    );
  }

  if (handoff) {
    return (
      <div className="mx-auto max-w-5xl p-6">
        <HandoffScreen
          item={handoff}
          names={names}
          onBack={() => setHandoff(null)}
          onChanged={() => {
            setHandoff(null);
            void refetch();
          }}
        />
      </div>
    );
  }

  async function markComplete() {
    if (!separationId) return;
    setBusy(true);
    try {
      await completeSeparation(separationId, null);
      toast.success('Separation closed');
      void refetch();
    } catch (err) {
      toast.error("Couldn't close it", { description: errorMessage(err, 'Try again.') });
    } finally {
      setBusy(false);
    }
  }

  async function publishTotal() {
    if (!separationId) return;
    const n = Number(payroll);
    if (!Number.isFinite(n) || n < 0) {
      toast.error('Enter the final pay total first');
      return;
    }
    setBusy(true);
    try {
      await publishPayrollTotal(separationId, n, null);
      toast.success('Published to Finance', {
        description: 'One sum. No per-person breakdown was sent.',
      });
      setPayroll('');
      void refetch();
    } catch (err) {
      toast.error("Couldn't publish", { description: errorMessage(err, 'Try again.') });
    } finally {
      setBusy(false);
    }
  }

  function exportChecklist() {
    const lines = items.map(
      (i) =>
        `${i.completed_at ? '[x]' : '[ ]'} ${OWNER_LABELS[i.owner_position_key] ?? i.owner_position_key} — ${i.label}${i.is_blocking ? ' (blocking)' : ''}`,
    );
    const blob = new Blob(
      [`Separation checklist — ${leaverName ?? separationId}\n\n${lines.join('\n')}\n`],
      { type: 'text/plain' },
    );
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${separationId}-checklist.txt`;
    a.click();
    URL.revokeObjectURL(url);
  }

  const tabs = canSeeConsolidated
    ? ([
        { id: 'consolidated' as const, label: 'Consolidated', count: items.length },
        { id: 'mine' as const, label: 'My items', count: mine.length },
        { id: 'owner' as const, label: 'By owner' },
        { id: 'blocking' as const, label: 'Blocking', count: blocking.length },
      ] as const)
    : ([{ id: 'mine' as const, label: 'My items', count: mine.length }] as const);

  const shown =
    effectiveTab === 'mine'
      ? mine
      : effectiveTab === 'blocking'
        ? blocking
        : items;

  return (
    <div className="mx-auto max-w-5xl space-y-4 p-6">
      <PageHeader
        eyebrow={
          <Link to="/people/separations" className="hover:underline">
            People › Separations
          </Link>
        }
        title={
          canSeeConsolidated
            ? `${separationId} · ${leaverName ?? ''}`
            : `Separation checklist — ${leaverName ?? 'your items'}`
        }
        meta={
          canSeeConsolidated && record ? (
            <span>
              {reasonLabel(record.separation_type)}
              {record.severance_offered && record.severance_length_days
                ? ` · ${record.severance_length_days}-day severance`
                : ''}{' '}
              · Last working day {fmtDate(record.last_working_day)}
              {record.severance_pay_through
                ? ` · pay through ${fmtDate(record.severance_pay_through)}`
                : ''}
              {record.severance_benefits_through
                ? ` · benefits through ${fmtDate(record.severance_benefits_through)}`
                : ''}{' '}
              · {doneCount} of {items.length} items complete
              {blocking.length > 0 ? ` · ${blocking.length} blocking` : ''}
            </span>
          ) : (
            <span>
              Your items · {mine.filter((i) => !i.completed_at).length} of {mine.length}{' '}
              open
            </span>
          )
        }
        actions={
          canSeeConsolidated && record ? (
            <>
              <EditDatesButton record={record} canEdit={canWriteHr} onSaved={refetch} />
              <Button
                variant="ghost"
                size="sm"
                icon={<Download className="h-4 w-4" />}
                onClick={exportChecklist}
              >
                Export the checklist
              </Button>
              <Button
                size="sm"
                loading={busy}
                disabled={blocking.length > 0 || record?.status === 'Completed'}
                onClick={() => void markComplete()}
              >
                {record?.status === 'Completed' ? 'Closed' : 'Mark separation complete'}
              </Button>
            </>
          ) : undefined
        }
      />

      {!canSeeConsolidated && (
        <p className="text-xs text-tcs-text-muted">
          You are seeing the items assigned to you. The reason, the documents and the pay
          figures stay with HR and the executives.
        </p>
      )}

      {canSeeConsolidated && record && (
        <MissingDateBanner record={record} canEdit={canWriteHr} onSaved={refetch} />
      )}

      {canSeeConsolidated && blocking.length > 0 && (
        <Card className="border-sentiment-critical-text/40 bg-sentiment-critical-fill p-4">
          <div className="flex items-start gap-2">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-sentiment-critical-text" />
            <div>
              <div className="text-sm font-medium text-sentiment-critical-text">
                {blocking.length} blocking item{blocking.length === 1 ? '' : 's'}
              </div>
              <p className="mt-1 text-sm text-tcs-text-secondary">
                {blocking
                  .map(
                    (b) =>
                      `${b.label} (${OWNER_LABELS[b.owner_position_key] ?? b.owner_position_key})`,
                  )
                  .join(' · ')}
                . This separation cannot be marked complete until they clear.
              </p>
            </div>
          </div>
        </Card>
      )}

      {canSeeConsolidated && record && (
        <>
          {record.separation_type === 'Terminated_For_Cause' &&
            /^n/i.test(record.performance_trail_complete ?? '') && (
              <Card className="border-sentiment-warning-text/40 bg-sentiment-warning-fill p-3 text-sm text-tcs-text-secondary">
                <strong className="text-sentiment-warning-text">
                  Performance trail incomplete.
                </strong>{' '}
                Recorded honestly rather than blocked. It stands as a standing warning on
                this record and in the quarterly HR review.
              </Card>
            )}
          {spec?.ceoAttestationRequired && (
            <Card className="p-3 text-sm text-tcs-text-secondary">
              <strong className="text-tcs-text">Discussed with the CEO</strong>{' '}
              {record.ceo_attested
                ? `— recorded${record.ceo_attested_on ? ` on ${fmtDate(record.ceo_attested_on)}` : ''}.`
                : '— NOT recorded.'}{' '}
              {record.ceo_attestation_note}
              <span className="mt-1 block text-xs text-tcs-text-muted">
                The CEO is a watcher on this record: notified, able to read, never a
                blocking approval.
              </span>
            </Card>
          )}
          {record.notes && (
            <Card className="p-3">
              <div className="text-sm font-medium text-tcs-text">Record notes</div>
              {/* Verbatim, one line per note. The payroll evidence for a
                  backfilled separation lives here, and it carries a warning on
                  one of the three about a payroll row that looks copied
                  forward — summarising it would lose exactly that. */}
              {record.notes.split('\n').map((line) => (
                <p key={line} className="mt-1 text-xs text-tcs-text-secondary">
                  {line}
                </p>
              ))}
            </Card>
          )}
          {record.document_folder_url && (
            <a
              href={record.document_folder_url}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1.5 text-sm text-tcs-text-secondary hover:underline"
            >
              <FileText className="h-4 w-4" /> Separation folder in Drive
            </a>
          )}
        </>
      )}

      <TabBar tabs={tabs} activeId={effectiveTab} onChange={(id) => setTab(id as TabId)} />

      {effectiveTab === 'owner' ? (
        <ByOwner items={items} names={names} onChanged={refetch} onOpenHandoff={setHandoff} />
      ) : effectiveTab === 'consolidated' ? (
        <ByGroup items={items} names={names} onChanged={refetch} onOpenHandoff={setHandoff} />
      ) : (
        <div className="space-y-2">
          {shown.length === 0 && (
            <Card className="p-6 text-sm text-tcs-text-secondary">
              {effectiveTab === 'blocking'
                ? 'Nothing is blocking. '
                : 'You hold no items on this separation.'}
            </Card>
          )}
          {shown.map((i) => (
            <ChecklistItemRow
              key={i.item_id}
              item={i}
              names={names}
              canAct
              onChanged={refetch}
              onOpenHandoff={setHandoff}
            />
          ))}
        </div>
      )}

      {canSeeConsolidated && record && !record.payroll_total_published_at && (
        <Card className="p-4">
          <h3 className="text-sm font-semibold text-tcs-text">
            Publish the payroll total to Finance
          </h3>
          <p className="mt-1 text-sm text-tcs-text-secondary">
            One sum — final prorated pay, PTO payout and the severance schedule combined.
            No wage rate and no per-person severance amount is sent; HR owns pay, Finance
            owns revenue.
          </p>
          <div className="mt-2 flex flex-wrap items-end gap-2">
            <label className="text-sm text-tcs-text-secondary">
              Total
              <input
                value={payroll}
                onChange={(e) => setPayroll(e.target.value)}
                inputMode="decimal"
                className="ml-2 w-40 rounded border border-tcs-border px-2 py-1 text-sm"
              />
            </label>
            <Button size="sm" loading={busy} onClick={() => void publishTotal()}>
              Publish to Finance
            </Button>
          </div>
        </Card>
      )}
    </div>
  );
}

function ByGroup({
  items,
  names,
  onChanged,
  onOpenHandoff,
}: {
  items: ChecklistItem[];
  names: Map<string, string>;
  onChanged: () => void;
  onOpenHandoff: (i: ChecklistItem) => void;
}) {
  const groups = GROUP_ORDER.map((g) => [g, items.filter((i) => i.item_group === g)] as const)
    .filter(([, list]) => list.length > 0);
  return (
    <div className="space-y-5">
      {groups.map(([group, list]) => (
        <section key={group}>
          <h2 className="mb-2 text-sm font-semibold text-tcs-text">
            {GROUP_LABELS[group] ?? group}
            <span className="ml-2 font-normal text-tcs-text-muted">{list.length}</span>
          </h2>
          <div className="space-y-2">
            {list.map((i) => (
              <ChecklistItemRow
                key={i.item_id}
                item={i}
                names={names}
                canAct
                onChanged={onChanged}
                onOpenHandoff={onOpenHandoff}
              />
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}

function ByOwner({
  items,
  names,
  onChanged,
  onOpenHandoff,
}: {
  items: ChecklistItem[];
  names: Map<string, string>;
  onChanged: () => void;
  onOpenHandoff: (i: ChecklistItem) => void;
}) {
  const owners = Array.from(new Set(items.map((i) => i.owner_position_key)));
  return (
    <div className="space-y-5">
      {owners.map((owner) => {
        const list = items.filter((i) => i.owner_position_key === owner);
        const open = list.filter((i) => !i.completed_at).length;
        const holder = list.find((i) => i.assigned_to_eid)?.assigned_to_eid;
        return (
          <section key={owner}>
            <h2 className="mb-2 text-sm font-semibold text-tcs-text">
              {OWNER_LABELS[owner] ?? owner}
              {holder && (
                <span className="ml-2 font-normal text-tcs-text-secondary">
                  · {names.get(holder) ?? holder}
                </span>
              )}
              <span className="ml-2 font-normal text-tcs-text-muted">
                {open} open of {list.length}
              </span>
            </h2>
            <div className="space-y-2">
              {list.map((i) => (
                <ChecklistItemRow
                  key={i.item_id}
                  item={i}
                  names={names}
                  canAct
                  onChanged={onChanged}
                  onOpenHandoff={onOpenHandoff}
                />
              ))}
            </div>
          </section>
        );
      })}
    </div>
  );
}
